import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import Cards from '../../../../leap/modules/cards'
import {useKlarna} from '../../../../components/klarna-pay-later/context'
import useOcapiSite from '../../../../hooks/useOcapiSite'
import {isHydrated} from '../../../../utils/utils'
import useProductPricing from '../../../../hooks/use-product-pricing'
import {useIntl} from 'react-intl'

const ProductUSP = ({
    contentAssetHtml = '',
    product,
    moduleId = 'product-usp-cockpit',
    variant = 'usp'
}) => {
    const {currency, displayPrice} = useProductPricing(product) || {}
    const {formatMessage} = useIntl()

    // duplicates logic from PayLater component. Is needed to know beforehands if there is a PayPal PayLater card.
    const {
        enablePaypalLater,
        paypalLaterClientID,
        uspPayLaterPosition,
        uspPayLaterShowHeadline,
        uspPayLaterIcon,
        uspPayLaterShowAll
    } = useOcapiSite().customPreferences

    // duplicates PayLater components logic to check beforehands if there is a Klarna PayLater card.
    const {options: klarnaOptions} = useKlarna()

    const payLaterData = useMemo(() => {
        const isPaypalReady = !!(
            enablePaypalLater &&
            paypalLaterClientID &&
            displayPrice > 0 &&
            currency
        )
        const isKlarnaReady = !!(klarnaOptions.enabled && displayPrice > 0)

        if (!isPaypalReady && !isKlarnaReady) return null

        return {
            amount: displayPrice,
            currency,
            paypalPlacement: 'product',
            klarnaPlacement: 'credit-promotion-badge',
            uspPayLaterShowAll
        }
    }, [
        displayPrice,
        currency,
        enablePaypalLater,
        paypalLaterClientID,
        klarnaOptions.enabled,
        uspPayLaterShowAll
    ])

    const cards = useMemo(() => {
        const parsedItems = parseUSPContent(contentAssetHtml)
        const filteredItems = parsedItems.filter((item) => item.text.length > 0)

        // Requirement: Total count (USPs + potential PayLater) must be >= 3
        const totalPotentialCount = filteredItems.length + (payLaterData ? 1 : 0)
        if (totalPotentialCount < 3) return []

        const limitedItems = filteredItems.slice(0, payLaterData ? 5 : 6)

        const baseCards = limitedItems.map((item) => ({
            icon: {name: item.icon},
            headline: item.headline,
            copy: {text: item.text},
            cta: null // Per ticket: remove linking
        }))

        if (payLaterData) {
            const payLaterCard = {
                id: 'paylater-card',
                payLater: payLaterData,
                headline: uspPayLaterShowHeadline
                    ? formatMessage({
                          defaultMessage: 'Flexible payment',
                          id: 'usp.pay_later.headline'
                      })
                    : null,
                icon: {name: uspPayLaterIcon}
            }
            if (uspPayLaterPosition === 'first') {
                baseCards.unshift(payLaterCard)
            } else {
                baseCards.push(payLaterCard)
            }
        }

        return baseCards
    }, [
        contentAssetHtml,
        formatMessage,
        payLaterData,
        uspPayLaterIcon,
        uspPayLaterPosition,
        uspPayLaterShowHeadline
    ])

    // since parseUSPContent only runs on the client we return null before hydration
    if (cards.length === 0 || !isHydrated()) return null

    return (
        <Cards
            moduleId={moduleId}
            moduleType="product-usp"
            variantCards={variant}
            headingLevel={2}
            cards={cards}
            count={cards.length}
            spacing="none"
            modulePadding="usp"
        />
    )
}

ProductUSP.propTypes = {
    contentAssetHtml: PropTypes.string,
    product: PropTypes.object,
    moduleId: PropTypes.string,
    variant: PropTypes.oneOf(['usp', 'uspSummaryStep'])
}

export default ProductUSP

/**
 * Parses a raw HTML string into a structured array of USP objects.
 * Note: It works only on the client-side and will return an empty array during
 * Server-Side Rendering (SSR) to prevent errors.
 *
 * @param {string} htmlString - The raw HTML content from a CMS content asset.
 * @returns {Array<{icon: string, headline: string, text: string}>} Array of USP items.
 */
const parseUSPContent = (htmlString) => {
    if (typeof window === 'undefined' || !htmlString) return []
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlString, 'text/html')
    const listItems = doc.querySelectorAll('li')

    return Array.from(listItems).map((li) => {
        const link = li.querySelector('a')
        return {
            icon: li.getAttribute('data-icon') || 'Check',
            headline: li.getAttribute('data-headline') || '',
            text: (link ? link.textContent : li.textContent || '').trim()
        }
    })
}
